/**
 * Frontend routing metadata for solutions.
 *
 * The AUTHORITATIVE catalog is the backend registry (solutions_registry.py), served by
 * GET /api/solutions. All checkbox UI renders from that response, so a newly registered
 * solution appears in the invite and Manage Solution Access modals with no change here.
 *
 * This file only holds what the backend cannot know: where each solution lives in the
 * React router. Registering a new solution means adding one entry below plus a
 * `solution` key on its sidebar item.
 *
 * Order matters: firstAvailableRoute() walks this list and lands the user on the first
 * solution they actually hold.
 */
import { MessageSquare, TrendingUp, ShoppingCart, Users } from "lucide-react";

/**
 * Adding a solution card later means appending ONE entry here — nothing else.
 * `key` and `path` drive routing (firstAvailableRoute); the rest is presentation for the
 * authenticated SynexOS Home page. Order is the display order and the landing precedence.
 */
export const SOLUTION_ROUTES = [
  {
    key: "workspace",
    path: "/workspace",
    name: "Workspace",
    icon: MessageSquare,
    description: "Enterprise collaboration platform.",
    features: [
      "Mail",
      "Chat",
      "Channels",
      "Tasks",
      "Approvals",
      "Calls",
      "Huddles",
      "Notifications",
    ],
  },
  {
    key: "revenue",
    path: "/commerce/revenue-workflow/leads",
    name: "Revenue",
    icon: TrendingUp,
    description: "Complete revenue lifecycle management.",
    features: ["Leads", "Evaluation", "Commit", "Proposal", "Contract", "Handoff"],
  },
  {
    key: "procurement",
    path: "/solutions/operations/procurement",
    name: "Procurement",
    icon: ShoppingCart,
    description: "Enterprise procurement and spend management.",
    features: [
      "Purchase Orders",
      "Reimbursements",
      "Advances",
      "MSME Priorities",
      "GST / TDS Compliance",
      "Payment Runs",
      "Budget Allocations",
      "Approval Chains",
    ],
  },
  {
    key: "workforce",
    path: "/solutions/workforce",
    name: "Workforce",
    icon: Users,
    description: "People, time, payroll, and lifecycle management.",
    features: [
      "Employee Directory",
      "Org Chart",
      "Time & Attendance",
      "Leave Management",
      "Statutory Payroll",
      "Self-Service Portal",
    ],
  },
];

/** The authenticated home for a member who holds no solutions. Never solution-gated. */
export const HOME_ROUTE = "/home";

/**
 * Where to send a user who lacks the solution they just asked for.
 *
 * Must never return a route that is itself gated on a solution the user lacks, or the
 * guards bounce. Falling through to HOME_ROUTE is the correct terminal state for a member
 * with `solutions: []`, which is a legal thing for an admin to configure.
 */
export function firstAvailableRoute(hasSolution) {
  const match = SOLUTION_ROUTES.find((s) => hasSolution(s.key));
  return match ? match.path : HOME_ROUTE;
}

/** True when the member holds at least one registered solution. */
export function hasAnySolution(hasSolution) {
  return SOLUTION_ROUTES.some((s) => hasSolution(s.key));
}
